import React, { useMemo, useState } from "react";
import { useSchoolSubjects } from "../../domains/schoolSubjects/useSchoolSubjects";
import { displaySubjectName } from "../../utils/subjectDisplayName";
import WormList from "./stats/WormList";

function normalizeSourceUrls(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((x) => x && x.url);
  return [];
}

function fmtEntries(n) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  return Number.isFinite(x) ? String(x) : "—";
}

function subjectItems(rows) {
  const qualifications = [...new Set(rows.map((row) => (row.qualification || "").trim()).filter(Boolean))];
  const shared = qualifications.length === 1 ? qualifications[0] : "";
  const items = [...rows]
    .sort((a, b) => {
      const left = Number(a.entries);
      const right = Number(b.entries);
      const aNum = Number.isFinite(left) ? left : -1;
      const bNum = Number.isFinite(right) ? right : -1;
      if (bNum !== aNum) return bNum - aNum;
      return displaySubjectName(a.subject_name).localeCompare(displaySubjectName(b.subject_name));
    })
    .map((row) => {
      const name = displaySubjectName(row.subject_name);
      const qual = (row.qualification || "").trim();
      return {
        key: row.id,
        label: name,
        note: shared || !qual || qual === name ? "" : qual,
        value: row.entries,
        display: fmtEntries(row.entries),
      };
    });
  return { items, shared };
}

function SubjectWorms({ rows, title }) {
  const { items, shared } = subjectItems(rows);
  return (
    <WormList
      title={shared ? `${title}. ${shared}.` : title}
      hint="Bar length is this subject’s entries beside the busiest subject in the list."
      items={items}
    />
  );
}

export default function SubjectsPanel({ schoolId, enabled }) {
  const { rows, loading, error } = useSchoolSubjects(schoolId, Boolean(enabled && schoolId));

  const { gcseRows, alevelRows, gcseFootnotes, sourceUrls } = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const gcse = [];
    const alevel = [];
    for (const r of list) {
      if (r.level === "alevel") alevel.push(r);
      else gcse.push(r);
    }
    const notes = [...new Set(gcse.map((r) => r.notes).filter(Boolean))];
    const first = list[0];
    const urls = normalizeSourceUrls(first?.source_urls);
    return { gcseRows: gcse, alevelRows: alevel, gcseFootnotes: notes, sourceUrls: urls };
  }, [rows]);

  if (!enabled) return null;

  if (loading) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted">Loading subjects…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted" style={{ color: "#c00" }}>
          {error}
        </div>
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted">No subject entries in data yet.</div>
      </div>
    );
  }

  return (
    <SubjectsTabs
      gcseRows={gcseRows}
      alevelRows={alevelRows}
      gcseFootnotes={gcseFootnotes}
      sourceUrls={sourceUrls}
    />
  );
}

function SubjectsTabs({ gcseRows, alevelRows, gcseFootnotes, sourceUrls }) {
  const [tab, setTab] = useState(gcseRows.length ? "gcse" : "alevel");
  const active = tab === "alevel" ? "alevel" : "gcse";

  return (
    <div className="v2-catchment-panel v2-subjects-panel">
      <div className="v2-catchment-year-toggles" role="tablist" aria-label="Subject level">
        <button
          type="button"
          role="tab"
          aria-selected={active === "gcse"}
          className={`v2-catchment-year-btn${active === "gcse" ? " v2-catchment-year-btn--active" : ""}`}
          onClick={() => setTab("gcse")}
        >
          GCSE
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={active === "alevel"}
          className={`v2-catchment-year-btn${active === "alevel" ? " v2-catchment-year-btn--active" : ""}`}
          onClick={() => setTab("alevel")}
        >
          A level
        </button>
      </div>

      <div className="v2-subjects-section">
        {active === "gcse" ? (
          gcseRows.length ? (
            <SubjectWorms rows={gcseRows} title="GCSE and equivalent entries" />
          ) : (
            <p className="v2-muted">No Key Stage 4 rows in DfE data for this school.</p>
          )
        ) : alevelRows.length ? (
          <SubjectWorms rows={alevelRows} title="A level and equivalent entries" />
        ) : (
          <p className="v2-muted">No post-16 subject rows in DfE data for this school (cohort in source file).</p>
        )}
        {active === "gcse" ? (
          <>
            <p className="v2-muted v2-subjects-dfe-footnote">
              <sup>1</sup> This list only includes qualifications that count towards the government&apos;s performance
              tables; IGCSEs, for example, which a lot of independent school pupils sit, are not included.
            </p>
            {gcseFootnotes.length ? (
              <div className="v2-subjects-context-notes">
                {gcseFootnotes.map((t) => (
                  <p key={t} className="v2-muted v2-subjects-context-note">
                    {t}
                  </p>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {sourceUrls.length ? (
        <div className="v2-detail-section v2-subjects-sources">
          <div className="v2-detail-section-title">Sources</div>
          <p className="v2-muted v2-subjects-sources-lead">
            Figures from DfE open data (Key stage 4 and 16–18 publications). Suppressed counts appear blank on Compare
            schools when cohorts are small.
          </p>
          <ul className="v2-subjects-source-list">
            {sourceUrls.map((s) => (
              <li key={s.url}>
                <a className="v2-drawer-link" href={s.url} target="_blank" rel="noreferrer">
                  {s.label || "Source"} →
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
